import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import Text from "../../components/Text";
import ItemStackView from "../../components/ItemStackView";
import CraftPlanView from "../../components/CraftPlanView";
import { Badge } from "../../components/Badge";
import { Skeleton } from "../../components/Skeleton";
import { Pagination } from "../../components/Pagination";
import { useToast } from "../../components/MessageBox";
import { useTranslation } from "../../hooks/useTranslation";
import { useItemStacks } from "../../hooks/useItemStacks";
import { useItemName } from "../../hooks/useItemName";
import { formatQuantity } from "../../services/quantityFormatter";
import { formatDuration } from "../../services/durationFormat";
import { STORAGE_ACTIONS } from "../../services/resourceActions";
import { useInventoryApi, type CraftOrderPlanLine, type CustomItemDefinition, type PlannedOrder, type StorageInfo } from "../../services/inventoryApi";
import shared from "./storageShared.module.css";
import styles from "./StoragePlannedOrders.module.css";

const LINES_PER_PAGE = 25;
// A storage with hundreds of low shelves plans hundreds of lines; past this many the card opens on the summary.
const COLLAPSE_ABOVE_LINES = 10;

interface Props {
  storage: StorageInfo;
  orders: PlannedOrder[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  onSubmitted: () => Promise<void>;
}

export default function StoragePlannedOrders(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inv = useInventoryApi();
  const { stackSize } = useItemName();

  const [customItems, setCustomItems] = createSignal<CustomItemDefinition[]>([]);
  const { nameFor, itemStack } = useItemStacks(customItems);
  const [expandedLineKeys, setExpandedLineKeys] = createSignal<string[]>([]);
  const [openedSourceIds, setOpenedSourceIds] = createSignal<Record<string, boolean>>({});
  const [pageBySourceId, setPageBySourceId] = createSignal<Record<string, number>>({});
  const [submittingSourceIds, setSubmittingSourceIds] = createSignal<string[]>([]);
  const [refreshing, setRefreshing] = createSignal(false);

  onMount(() => {
    inv.fetchCustomItems().then(setCustomItems).catch(() => {});
  });

  const mayWrite = () => props.storage.myActions.includes(STORAGE_ACTIONS.ItemsWrite);
  const ordersWithLines = createMemo(() => props.orders.filter(order => order.plan.lines.length > 0));

  const lineCraftable = (order: PlannedOrder, line: CraftOrderPlanLine) =>
    !order.sourceError && !line.planError && line.shortfalls.length === 0;
  const craftableLines = (order: PlannedOrder) => order.plan.lines.filter(line => lineCraftable(order, line));
  const submittableLines = (order: PlannedOrder) =>
    order.plan.eligibleBotIds.length > 0 ? craftableLines(order) : [];
  const submittableOrders = createMemo(() => ordersWithLines().filter(order => submittableLines(order).length > 0));

  const sourceName = (order: PlannedOrder) => order.sourceStorageName ?? t("STORAGE_STOCK_SOURCE_HIDDEN");
  const orderBlocker = (order: PlannedOrder) =>
    order.sourceError ?? (order.plan.eligibleBotIds.length === 0 ? t("INVENTORY_CRAFT_NO_SHARED_BOT") : null);

  const lineBlocker = (order: PlannedOrder, line: CraftOrderPlanLine) => {
    if (order.sourceError) return t("STORAGE_PLANNED_LINE_UNPLANNED");
    if (line.planError) return line.planError;
    const shortest = line.shortfalls[0];
    if (!shortest) return null;
    const ingredientStackSize = stackSize(shortest.itemKey);
    return t("STORAGE_PLANNED_LINE_SHORT", nameFor(shortest.itemKey),
      formatQuantity(shortest.needed, ingredientStackSize), formatQuantity(shortest.available, ingredientStackSize));
  };

  const lineKey = (order: PlannedOrder, line: CraftOrderPlanLine) => `${order.sourceStorageId}|${line.itemKey}`;
  const toggleExpanded = (key: string) =>
    setExpandedLineKeys(current => (current.includes(key) ? current.filter(expanded => expanded !== key) : [...current, key]));

  const linesOpen = (order: PlannedOrder) =>
    openedSourceIds()[order.sourceStorageId] ?? order.plan.lines.length <= COLLAPSE_ABOVE_LINES;
  const toggleLines = (order: PlannedOrder) =>
    setOpenedSourceIds(current => ({ ...current, [order.sourceStorageId]: !linesOpen(order) }));

  const pageOf = (order: PlannedOrder) => pageBySourceId()[order.sourceStorageId] ?? 0;
  const setPageOf = (order: PlannedOrder, page: number) =>
    setPageBySourceId(current => ({ ...current, [order.sourceStorageId]: page }));
  const totalPagesOf = (order: PlannedOrder) => Math.ceil(order.plan.lines.length / LINES_PER_PAGE);
  const pageLines = (order: PlannedOrder) => {
    const page = Math.min(pageOf(order), Math.max(0, totalPagesOf(order) - 1));
    return order.plan.lines.slice(page * LINES_PER_PAGE, (page + 1) * LINES_PER_PAGE);
  };

  async function refresh() {
    setRefreshing(true);
    try {
      await props.onRefresh();
    } finally {
      setRefreshing(false);
    }
  }

  async function submitOrders(orders: PlannedOrder[]) {
    setSubmittingSourceIds(orders.map(order => order.sourceStorageId));
    try {
      for (const order of orders) {
        const lines = submittableLines(order);
        try {
          await inv.createCraftOrder({
            sourceStorageId: order.sourceStorageId,
            resultStorageId: props.storage.id,
            lines: lines.map(line => ({ itemKey: line.itemKey, quantity: line.quantity })),
          });
          toast?.success(lines.length === 1
            ? t("STORAGE_PLANNED_SUBMITTED_ONE", sourceName(order))
            : t("STORAGE_PLANNED_SUBMITTED", sourceName(order), lines.length));
        } catch (e: any) {
          toast?.error(e?.message ?? t("STORAGE_PLANNED_SUBMIT_FAILED", sourceName(order)));
        }
      }
      await props.onSubmitted();
    } finally {
      setSubmittingSourceIds([]);
    }
  }

  const submitting = () => submittingSourceIds().length > 0;

  return (
    <Show when={!props.loading} fallback={<div class={`fcard ${styles.section}`}><Skeleton variant="text" count={3} /></div>}>
      <Show when={ordersWithLines().length > 0}>
        <section class={styles.section}>
          <div class={styles.sectionHead}>
            <div class="v" style={{ gap: "var(--spacing-xs)" }}>
              <span class={shared.sectionTitle} style={{ margin: 0 }}><Text val="STORAGE_PLANNED_TITLE" /></span>
              <span class={shared.fieldHint}><Text val="STORAGE_PLANNED_HINT" /></span>
            </div>
            <div class={styles.sectionActions}>
              <button class="btn btn-secondary btn-sm" disabled={refreshing() || submitting()} onClick={() => void refresh()}>
                <i class={`fa-solid ${refreshing() ? "fa-spinner fa-spin" : "fa-rotate"}`} /> <Text val="REFRESH" />
              </button>
              <Show when={mayWrite() && submittableOrders().length > 1}>
                <button class="btn btn-secondary btn-sm" disabled={submitting()} onClick={() => void submitOrders(submittableOrders())}>
                  <i class={`fa-solid ${submitting() ? "fa-spinner fa-spin" : "fa-hammer"}`} /> <Text val="STORAGE_PLANNED_SUBMIT_ALL" />
                </button>
              </Show>
            </div>
          </div>

          <For each={ordersWithLines()}>
            {(order) => (
              <div class={`fcard ${styles.card}`}>
                <div class={styles.cardHead}>
                  <i class={`fa-solid ${order.sourceStorageId === props.storage.id ? "fa-box" : "fa-truck-ramp-box"} ${styles.sourceIcon}`} />
                  <span class={styles.sourceName}>{t("STORAGE_PLANNED_FROM", sourceName(order))}</span>
                  <span class={styles.meta}>
                    {order.plan.lines.length === 1 ? t("STORAGE_PLANNED_LINE_COUNT_ONE") : t("STORAGE_PLANNED_LINE_COUNT", order.plan.lines.length)}
                  </span>
                  <span class={styles.meta}>{t("STORAGE_PLANNED_CRAFTABLE_COUNT", craftableLines(order).length)}</span>
                  <Show when={order.plan.lines.length - craftableLines(order).length > 0}>
                    <span class={`${styles.meta} ${styles.metaBlocked}`}>
                      {t("STORAGE_PLANNED_BLOCKED_COUNT", order.plan.lines.length - craftableLines(order).length)}
                    </span>
                  </Show>
                  <Show when={formatDuration(order.plan.estimatedSeconds)}>
                    {(eta) => <span class={styles.meta}>{t("INVENTORY_CRAFT_STEP_ETA", eta())}</span>}
                  </Show>
                  <button type="button" class={`btn btn-secondary btn-sm ${styles.linesToggle}`} onClick={() => toggleLines(order)}>
                    <i class={`fa-solid ${linesOpen(order) ? "fa-chevron-up" : "fa-chevron-down"}`} />
                    {" "}<Text val={linesOpen(order) ? "STORAGE_PLANNED_HIDE_LINES" : "STORAGE_PLANNED_SHOW_LINES"} />
                  </button>
                </div>

                <Show when={orderBlocker(order)}>
                  {(blocker) => <p class={styles.blocking}><i class="fa-solid fa-triangle-exclamation" /> {blocker()}</p>}
                </Show>

                <Show when={linesOpen(order)}>
                  <div class={styles.lines}>
                    <For each={pageLines(order)}>
                      {(line) => (
                        <div classList={{ [styles.line]: true, [styles.lineBlocked]: !lineCraftable(order, line) }}>
                          <div class={styles.lineHead}>
                            <ItemStackView stack={itemStack(line.itemKey)} size={24} bare showTooltip />
                            <span class={styles.lineName}>{nameFor(line.itemKey)}</span>
                            <span class={styles.lineQuantity}>{formatQuantity(line.quantity, stackSize(line.itemKey))}</span>
                            <div class={styles.lineStatus}>
                              <Show when={lineBlocker(order, line)} fallback={
                                <Badge tone="success"><Text val="STORAGE_PLANNED_LINE_CRAFTABLE" /></Badge>
                              }>
                                {(blocker) => <span class={styles.lineReason}>{blocker()}</span>}
                              </Show>
                              <Show when={!order.sourceError}>
                                <button type="button" class="btn btn-secondary btn-sm" onClick={() => toggleExpanded(lineKey(order, line))}>
                                  <Text val={expandedLineKeys().includes(lineKey(order, line)) ? "INVENTORY_CRAFT_ORDER_HIDE_PLAN" : "INVENTORY_CRAFT_ORDER_SHOW_PLAN"} />
                                </button>
                              </Show>
                            </div>
                          </div>

                          <Show when={!order.sourceError && expandedLineKeys().includes(lineKey(order, line))}>
                            <div class={styles.linePlan}>
                              <CraftPlanView
                                steps={line.steps}
                                shortfalls={line.shortfalls}
                                totalConsumed={line.totalConsumed}
                                estimatedSeconds={line.estimatedSeconds}
                                planError={line.planError}
                                customItems={customItems()}
                              />
                            </div>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                  <Pagination page={pageOf(order)} totalPages={totalPagesOf(order)} onChange={page => setPageOf(order, page)} size="sm" />
                </Show>

                <div class={styles.cardFoot}>
                  <Show when={!mayWrite() || submittableLines(order).length === 0}>
                    <span class={shared.fieldHint}>
                      <Text val={mayWrite() ? "STORAGE_PLANNED_NOTHING_CRAFTABLE" : "STORAGE_PLANNED_READ_ONLY"} />
                    </span>
                  </Show>
                  <button
                    class="btn btn-primary btn-sm"
                    disabled={!mayWrite() || submitting() || submittableLines(order).length === 0}
                    onClick={() => void submitOrders([order])}
                  >
                    <i class={`fa-solid ${submittingSourceIds().includes(order.sourceStorageId) ? "fa-spinner fa-spin" : "fa-hammer"}`} />
                    {" "}<Text val="STORAGE_PLANNED_SUBMIT" />
                  </button>
                </div>
              </div>
            )}
          </For>
        </section>
      </Show>
    </Show>
  );
}
